import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { themeClasses } from '../../utils/themeClasses';
import { 
  Trophy, 
  Users, 
  BarChart3, 
  TrendingUp, 
  Award,
  Calendar,
  MapPin,
  Star,
  ArrowRight,
  Zap,
  Target,
  Shield
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import HeroSection from '../Hero/HeroSection';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { championshipsApi, teamsApi, championsApi, gamesApi } from '../../services/api';

const ModernDashboard: React.FC = () => {
  const siteConfig = useSiteConfig();

  // Fetch aggregate data from API - no season filtering for home page stats
  const { data: championships } = useQuery({
    queryKey: ['championships-all'],
    queryFn: async () => {
      const response = await championshipsApi.getAll();
      return response.data;
    }
  });

  const { data: teams } = useQuery({
    queryKey: ['teams-all'],
    queryFn: async () => {
      const response = await teamsApi.getAll();
      return response;
    }
  });

  const { data: allChampions } = useQuery({
    queryKey: ['champions-all'],
    queryFn: async () => {
      // Get all champions across all years
      const response = await championsApi.getAll();
      return response.data;
    }
  });

  const { data: recentChampions } = useQuery({
    queryKey: ['champions-recent'],
    queryFn: async () => {
      // Get recent champions for the table (last year)
      const currentYear = new Date().getFullYear();
      const response = await championsApi.getByYear(currentYear - 1);
      return response.data;
    }
  });

  const { data: games } = useQuery({
    queryKey: ['games-recent'],
    queryFn: async () => {
      const response = await gamesApi.getRecent();
      return response.data;
    }
  });
  
  const stats = [
    {
      label: 'Total Championships',
      value: championships?.length?.toString() || '0',
      change: 'Tracked',
      icon: Trophy,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: 'Teams Tracked',
      value: teams?.length?.toString() || '0',
      change: 'Active',
      icon: Users,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
    {
      label: 'Total Champions',
      value: allChampions?.length?.toString() || '0',
      change: 'All-Time',
      icon: BarChart3,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      label: 'Recent Games',
      value: games?.length?.toString() || '0',
      change: 'Updated',
      icon: Zap,
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
  ];

  const features = [
    {
      icon: Target,
      title: 'ADVANCED ANALYTICS',
      description: 'AI-powered insights to understand championship patterns and team performance like never before.',
      link: '/analytics',
      gradient: 'from-primary to-secondary'
    },
    {
      icon: Shield,
      title: 'OPTIMIZED FOR ACCURACY',
      description: 'Comprehensive data validation ensures every stat, score, and record is verified and accurate.',
      link: '/data-quality',
      gradient: 'from-secondary to-accent'
    },
    {
      icon: TrendingUp,
      title: 'BUILT TO SCALE',
      description: 'From local leagues to professional championships, our platform grows with your data needs.',
      link: '/features',
      gradient: 'from-accent to-primary'
    }
  ];

  // Transform API data to display format
  const championsList = recentChampions?.slice(0, 5).map(champ => ({
    year: champ.year,
    team: `${champ.team_city} ${champ.team_name}`,
    championship: champ.championship_name,
    score: champ.record || 'W',
    opponent: '', // API doesn't provide opponent directly
    sport: champ.championship_name?.includes('Super Bowl') ? 'NFL' :
           champ.championship_name?.includes('NBA') ? 'NBA' :
           champ.championship_name?.includes('World Series') ? 'MLB' : 'Sports'
  })) || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection />

      {/* Stats Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <span className="gradient-text">PLATFORM STATISTICS</span>
            </h2>
            <p className="text-xl text-gray-400">
              Comprehensive data across all championships and seasons
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-6 group hover:scale-105 transition-all duration-300"
                  style={{
                    borderColor: index === 0 ? 'rgba(0, 255, 136, 0.3)' : undefined
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                    <span className="text-xs text-primary font-bold px-2 py-1 bg-primary/10 rounded-full">
                      {stat.change}
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative bg-dots-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="badge badge-primary mb-4 inline-block">FEATURES</span>
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              <span className="text-white">ACTIONABLE </span>
              <span className={themeClasses.neonText}>ANALYTICS</span>
            </h2>
            <h3 className="text-3xl md:text-4xl font-black mb-6">
              <span className="text-white">FOR YOUR </span>
              <span className="gradient-text">SPORTS DATA</span>
            </h3>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              With Sports Machine, you have access to valuable championship data 
              from every major league and tournament at a glance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="feature-card relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-2xl"
                    style={{
                      background: `linear-gradient(135deg, var(--tw-gradient-stops))`
                    }}
                  />
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-4 uppercase tracking-wider">
                      {feature.title}
                    </h3>
                    
                    <p className="text-gray-400 mb-6 leading-relaxed">
                      {feature.description}
                    </p>
                    
                    <Link 
                      to={feature.link}
                      className="inline-flex items-center space-x-2 text-primary hover:text-primary-dark transition-colors"
                    >
                      <span className="font-semibold">LEARN MORE</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent Champions Table */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-4xl font-black mb-4">
              <span className="gradient-text">RECENT CHAMPIONS</span>
            </h2>
            <p className="text-xl text-gray-400">
              Latest championship victories across major sports leagues
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="data-table"
          >
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left">Year</th>
                  <th className="text-left">Team</th>
                  <th className="text-left">Championship</th>
                  <th className="text-left">Score</th>
                  <th className="text-left">Opponent</th>
                  <th className="text-center">League</th>
                </tr>
              </thead>
              <tbody>
                {championsList.length > 0 ? (
                  championsList.map((champion, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <td className="font-bold text-primary">{champion.year}</td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <Trophy className="w-4 h-4 text-primary/60" />
                          <span className="font-semibold">{champion.team}</span>
                        </div>
                      </td>
                      <td className="text-gray-300">{champion.championship}</td>
                      <td>
                        <span className="badge badge-primary">{champion.score}</span>
                      </td>
                      <td className="text-gray-400">{champion.opponent}</td>
                      <td className="text-center">
                        <span className="badge badge-secondary">{champion.sport}</span>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-400">
                      <div className="flex flex-col items-center space-y-2">
                        <Trophy className="w-8 h-8 text-gray-500" />
                        <span>Loading championship data...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <Link to="/champions" className="btn-neon-outline inline-flex items-center space-x-2">
              <span>VIEW ALL CHAMPIONS</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Sports Machine Network Section */}
      <section className="py-20 relative bg-dots-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge badge-primary mb-4 inline-block">NETWORK</span>
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-white">THE SPORTS MACHINE </span>
              <span className="gradient-text">NETWORK</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Specialized platforms for every major sport and level. Choose your passion.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Professional Sports */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="col-span-1 lg:col-span-3"
            >
              <h3 className="text-2xl font-bold text-white mb-6 text-center">PROFESSIONAL SPORTS</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { name: 'The Football Machine', sport: 'Professional Football', url: 'https://www.the-football-machine.com', target: '_blank', color: 'from-blue-500 to-red-500' },
                  { name: 'The Basketball Machine', sport: 'Professional Basketball', url: 'https://www.the-basketball-machine.com', target: '_blank', color: 'from-red-500 to-blue-500' },
                  { name: 'The Baseball Machine', sport: 'Professional Baseball', url: 'https://www.the-baseball-machine.com', target: '_blank', color: 'from-green-500 to-white' },
                  { name: 'The Hockey Machine', sport: 'Professional Hockey', url: 'https://www.the-hockey-machine.com', target: '_blank', color: 'from-black to-yellow-500' },
                ].map((site, index) => (
                  <motion.a
                    key={site.name}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="glass-card p-6 group hover:scale-105 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${site.color} opacity-5 group-hover:opacity-10 transition-opacity`} />
                    <div className="relative z-10">
                      <Trophy className="w-8 h-8 text-primary mb-3" />
                      <h4 className="font-bold text-white mb-1">{site.name}</h4>
                      <p className="text-sm text-gray-400">{site.sport}</p>
                    </div>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* College Sports */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="col-span-1 lg:col-span-3"
            >
              <h3 className="text-2xl font-bold text-white mb-6 text-center">COLLEGE SPORTS</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { name: 'The College Football Machine', sport: 'College Football', url: 'https://www.the-college-football-machine.com', target: '_blank', color: 'from-purple-500 to-gold-500' },
                  { name: 'The College Basketball Machine', sport: 'College Basketball', url: 'https://www.the-college-basketball-machine.com', target: '_blank', color: 'from-orange-500 to-blue-500' },
                  { name: 'The College Baseball Machine', sport: 'College Baseball', url: 'https://www.the-college-baseball-machine.com', target: '_blank', color: 'from-green-500 to-red-500' },
                  { name: 'The College Hockey Machine', sport: 'College Hockey', url: 'https://www.the-college-hockey-machine.com', target: '_blank', color: 'from-blue-800 to-white' },
                ].map((site, index) => (
                  <motion.a
                    key={site.name}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="glass-card p-6 group hover:scale-105 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${site.color} opacity-5 group-hover:opacity-10 transition-opacity`} />
                    <div className="relative z-10">
                      <Award className="w-8 h-8 text-secondary mb-3" />
                      <h4 className="font-bold text-white mb-1">{site.name}</h4>
                      <p className="text-sm text-gray-400">{site.sport}</p>
                    </div>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sport-Specific Content Section */}
      {siteConfig.sport && (
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-4xl font-black mb-4">
                <span className="gradient-text">
                  {siteConfig.sport === 'football' && siteConfig.level === 'college' ? 'COLLEGE FOOTBALL HIGHLIGHTS' :
                   siteConfig.sport === 'basketball' && siteConfig.level === 'college' ? 'MARCH MADNESS MOMENTS' :
                   siteConfig.sport === 'baseball' && siteConfig.level === 'college' ? 'ROAD TO OMAHA' :
                   siteConfig.sport === 'hockey' && siteConfig.level === 'college' ? 'FROZEN FOUR GLORY' :
                   siteConfig.sport === 'football' ? 'NFL CHAMPIONSHIP LEGACY' :
                   siteConfig.sport === 'basketball' ? 'NBA DYNASTY TRACKER' :
                   siteConfig.sport === 'baseball' ? 'WORLD SERIES HISTORY' :
                   siteConfig.sport === 'hockey' ? 'STANLEY CUP CHAMPIONS' :
                   'CHAMPIONSHIP EXCELLENCE'}
                </span>
              </h2>
              <p className="text-xl text-gray-400">
                Dive deep into the rich history and statistics of {siteConfig.name}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="glass-card p-6"
              >
                <Star className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">All-Time Records</h3>
                <p className="text-gray-400 mb-4">
                  Explore championship records, winning streaks, and historic achievements.
                </p>
                <Link to="/champions" className="text-primary hover:text-primary-dark transition-colors">
                  View Records →
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="glass-card p-6"
              >
                <BarChart3 className="w-8 h-8 text-secondary mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Statistical Analysis</h3>
                <p className="text-gray-400 mb-4">
                  Advanced metrics and analytics for teams, players, and seasons.
                </p>
                <Link to="/standings" className="text-primary hover:text-primary-dark transition-colors">
                  View Stats →
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="glass-card p-6"
              >
                <Calendar className="w-8 h-8 text-accent mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Season Archives</h3>
                <p className="text-gray-400 mb-4">
                  Complete season-by-season results and championship outcomes.
                </p>
                <Link to="/seasons" className="text-primary hover:text-primary-dark transition-colors">
                  Browse Seasons →
                </Link>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12 text-center relative overflow-hidden"
          >
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-radial from-primary/10 to-transparent" />
            
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black mb-6">
                <span className="text-white">READY TO GET </span>
                <span className={themeClasses.neonText}>STARTED?</span>
              </h2>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                {siteConfig.sport ? 
                  `Access comprehensive ${siteConfig.name} data, statistics, and championship history.` :
                  'With Sports Machine, you can easily get comprehensive championship data from all major sports leagues and tournaments.'}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Link to="/championships" className="btn-neon flex items-center space-x-2">
                  <Trophy className="w-5 h-5" />
                  <span>EXPLORE CHAMPIONSHIPS</span>
                </Link>
                <Link to="/analytics" className="btn-neon-outline flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>VIEW ANALYTICS</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ModernDashboard;